import React from "react";
import { useOffline } from "@/contexts/OfflineContext";
import { WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";

// Top sticky banner.
//  - Offline      → amber, "No internet. You can still view your tree — edits will queue."
//  - Online + pending → green, "N changes sync ho rahe hain…"
//  - Online + no pending → nothing.
export function OfflineBanner() {
    const { isOnline, pendingCount, syncing, syncNow } = useOffline();

    if (isOnline && pendingCount === 0 && !syncing) return null;

    if (!isOnline) {
        return (
            <div
                data-testid="offline-banner"
                role="status"
                className="sticky top-0 z-[55] w-full bg-[#FFF3E0] border-b border-[#F57C00]/40 text-[#7A3E00] text-[13.5px] px-4 py-2 flex items-center justify-center gap-2 shadow-sm"
            >
                <WifiOff className="h-4 w-4 shrink-0" />
                <span className="font-medium">
                    You're offline — showing the last loaded family tree.
                    {pendingCount > 0 && (
                        <span className="ml-1 font-semibold" data-testid="offline-pending-count">
                            {pendingCount} change{pendingCount === 1 ? "" : "s"} queued
                        </span>
                    )}
                </span>
            </div>
        );
    }

    // Online with pending queue or actively syncing
    return (
        <div
            data-testid="offline-syncing-banner"
            role="status"
            className="sticky top-0 z-[55] w-full bg-[#E8F5E9] border-b border-[#1B5E20]/30 text-[#1B5E20] text-[13.5px] px-4 py-2 flex items-center justify-center gap-2 shadow-sm"
        >
            {syncing ? (
                <RefreshCw className="h-4 w-4 animate-spin shrink-0" />
            ) : (
                <CheckCircle2 className="h-4 w-4 shrink-0" />
            )}
            <span className="font-medium">
                {syncing
                    ? `Syncing${pendingCount ? ` — ${pendingCount} remaining` : ""}…`
                    : `${pendingCount} change${pendingCount === 1 ? "" : "s"} pending sync`}
            </span>
            {!syncing && (
                <button
                    type="button"
                    data-testid="offline-sync-now"
                    onClick={syncNow}
                    className="ml-2 underline underline-offset-2 hover:no-underline font-semibold"
                >
                    Sync now
                </button>
            )}
        </div>
    );
}

export default OfflineBanner;
